import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { querySelectorAllAsArray } from '../../../utils/query-selector-all-as-array';
import { isSlotted } from '../../../utils/is-slotted';
import { DropdownItemSelectionChangeDetail } from '../dropdown.types';

/**
 * @slot - label of the selection item has to be placed here (required)
 * @slot button - Pass button to list item (optional)
 * @slot meta - Pass additional data to list item (optional)
 */
@Component({
  tag: 'wb-dropdown-list-item',
  styleUrl: './dropdown-list-item.scss',
  shadow: true,
})
export class DropdownListItemComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** The item value. */
  @Prop({ reflect: true })
  public value?: string;

  /** Status of selection. */
  @Prop({ reflect: true, mutable: true })
  public selected: boolean;

  /** Indicates if item is disabled. */
  @Prop({ reflect: true })
  public disabled?: boolean;

  /** Emitted when the selection changes. */
  @Event()
  public wbdropdownlistitemselectionchange: EventEmitter<DropdownItemSelectionChangeDetail>;

  @Watch('selected')
  public selectedChanged(): void {
    this.handleSelected();
  }

  private dropdownListEl: HTMLWbDropdownListElement;
  private TagNames = transformTagNames(this, 'wb-dropdown-list-item', ['wb-icon', 'wb-list-group-item']);
  private content: string;
  private hasButton: boolean;

  private handleSelected(): void {
    if (this.selected) {
      const index = querySelectorAllAsArray(this.dropdownListEl, this.el.tagName).findIndex((child) => child === this.el);
      this.wbdropdownlistitemselectionchange.emit({ index, value: this.value, content: this.content });
    }
  }

  private handleClick = (): void => {
    if (!this.hasButton) {
      this.selected = true;
    }
  };

  private setContent(slot: HTMLSlotElement): void {
    if (slot.assignedElements().length) {
      this.content = slot.assignedElements()[0].outerHTML;
    } else {
      this.content = slot.assignedNodes()[0].textContent.trim();
    }
  }

  private onSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    this.setContent(slot);
    this.hasButton = isSlotted(this.el, 'button');

    this.dropdownListEl = this.el.parentElement as HTMLWbDropdownListElement;
    this.handleSelected();
  };

  public render(): JSX.Element {
    const { WbIcon, WbListGroupItem } = this.TagNames;

    return (
      <Host class="wb-dropdown-list-item">
        <WbListGroupItem interactive={!this.disabled && !this.hasButton} onClick={this.handleClick}>
          {this.hasButton && <slot slot="button" name="button" />}
          <slot onSlotchange={this.onSlotChange} />
          {!this.hasButton && <slot slot="meta" name="meta" />}
          {this.selected && !this.hasButton && <WbIcon slot="meta-icon" name="bds/checkmark/24"></WbIcon>}
        </WbListGroupItem>
      </Host>
    );
  }
}
